import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, X, BookOpen, PlayCircle, LifeBuoy } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { findArticleByPath } from './articles';
import { ArticleBody } from './ArticleBody';
import { useTour } from './TourProvider';

// Botão flutuante de ajuda (?) + drawer lateral com o artigo da tela atual.
export function HelpLauncher() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { startTour, tourForCurrentRoute } = useTour();

  const article = findArticleByPath(location.pathname);
  const routeTour = tourForCurrentRoute();
  const Icon = article?.icon ?? LifeBuoy;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          aria-label="Ajuda desta tela"
          title="Ajuda desta tela"
          className="fixed bottom-5 right-5 z-40 grid h-12 w-12 place-items-center rounded-full gradient-brand text-white shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
        >
          <HelpCircle className="h-6 w-6" />
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed right-0 top-0 z-50 flex h-full w-[92vw] max-w-md flex-col bg-card shadow-2xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          )}
        >
          {/* header */}
          <div className="flex items-start gap-3 border-b border-border p-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-brand-100 text-brand-700">
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <Dialog.Title className="text-base font-semibold text-foreground">
                {article ? article.title : 'Central de Ajuda'}
              </Dialog.Title>
              <Dialog.Description className="text-xs text-muted-foreground">
                {article ? article.summary : 'Escolha um tópico para começar.'}
              </Dialog.Description>
            </div>
            <Dialog.Close className="text-muted-foreground hover:text-foreground" aria-label="Fechar">
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          {/* corpo */}
          <div className="flex-1 overflow-y-auto p-4">
            {article ? (
              <ArticleBody blocks={article.body} />
            ) : (
              <p className="text-sm text-muted-foreground">
                Não há um guia específico para esta tela. Abra a Central de Ajuda para ver todos os tópicos.
              </p>
            )}
          </div>

          {/* rodapé com ações */}
          <div className="space-y-2 border-t border-border p-4">
            {routeTour && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setOpen(false); startTour(routeTour.id); }}
              >
                <PlayCircle className="h-4 w-4" /> Refazer o tour desta tela
              </Button>
            )}
            <Button
              variant="gradient"
              className="w-full"
              onClick={() => { setOpen(false); navigate('/ajuda'); }}
            >
              <BookOpen className="h-4 w-4" /> Abrir a Central de Ajuda
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
